// Sopwith 3D - particle effects.
//
// A single pool of point sprites used for explosion sparks, bomb-hit dust
// and gunfire flashes. Particles are purely cosmetic: the simulation's own
// debris objects still drive what actually hits things.

import * as THREE from 'three';

const MAX = 2048;

export class Particles {
  readonly points: THREE.Points;
  private readonly pos = new Float32Array(MAX * 3);
  private readonly col = new Float32Array(MAX * 3);
  private readonly vel = new Float32Array(MAX * 3);
  private readonly life = new Float32Array(MAX);
  private readonly maxLife = new Float32Array(MAX);
  private readonly size = new Float32Array(MAX);
  private next = 0;
  private readonly geometry: THREE.BufferGeometry;

  constructor() {
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.col, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.size, 1));
    const material = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `
        attribute float size;
        varying vec3 vColor;
        void main() {
          vColor = color;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mv.z);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vColor;
        void main() {
          float d = length(gl_PointCoord - 0.5);
          if (d > 0.5) discard;
          float a = smoothstep(0.5, 0.15, d);
          gl_FragColor = vec4(vColor, a);
        }
      `,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    this.points = new THREE.Points(this.geometry, material);
    this.points.frustumCulled = false;
    this.geometry.setDrawRange(0, MAX);
    this.life.fill(0);
    this.size.fill(0);
  }

  emit(
    x: number,
    y: number,
    z: number,
    count: number,
    speed: number,
    color: THREE.Color,
    lifeSec: number,
    size: number,
    spread = 1,
  ): void {
    for (let n = 0; n < count; n++) {
      const i = this.next;
      this.next = (this.next + 1) % MAX;
      const a = Math.random() * Math.PI * 2;
      const b = (Math.random() - 0.5) * Math.PI * spread;
      const s = speed * (0.4 + Math.random() * 0.8);
      this.pos[i * 3] = x + (Math.random() - 0.5) * 2;
      this.pos[i * 3 + 1] = y + (Math.random() - 0.5) * 2;
      this.pos[i * 3 + 2] = z + (Math.random() - 0.5) * 6;
      this.vel[i * 3] = Math.cos(a) * Math.cos(b) * s;
      this.vel[i * 3 + 1] = Math.sin(a) * Math.cos(b) * s + speed * 0.3;
      this.vel[i * 3 + 2] = Math.sin(b) * s;
      const tint = 0.7 + Math.random() * 0.6;
      this.col[i * 3] = color.r * tint;
      this.col[i * 3 + 1] = color.g * tint;
      this.col[i * 3 + 2] = color.b * tint;
      this.life[i] = this.maxLife[i] = lifeSec * (0.6 + Math.random() * 0.6);
      this.size[i] = size * (0.6 + Math.random() * 0.8);
    }
  }

  update(dt: number): void {
    const g = -90 * dt;
    for (let i = 0; i < MAX; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this.size[i] = 0;
        continue;
      }
      this.vel[i * 3 + 1] += g;
      this.pos[i * 3] += this.vel[i * 3] * dt;
      this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt;
      this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
      const t = this.life[i] / this.maxLife[i];
      this.size[i] = this.size[i] * (0.97 + t * 0.03);
    }
    (this.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.getAttribute('size') as THREE.BufferAttribute).needsUpdate = true;
  }
}

export const FIRE = new THREE.Color(2.6, 1.2, 0.3);
export const SPARK = new THREE.Color(2.4, 2.2, 1.2);
export const DUST = new THREE.Color(0.55, 0.45, 0.3);
export const MUZZLE = new THREE.Color(2.0, 1.8, 0.9);
